import { useCallback, useEffect, useState } from "react";
import { CRMLayout } from "@/components/CRMLayout";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Mail, Paperclip, Plug, Send, X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const champ = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm";

function Connexion({ etat, token, onConnecte }) {
  const [adresse, setAdresse] = useState(etat?.adresse || "contact.ccrm1234@gmail.com");
  const [nom, setNom] = useState(etat?.nom && etat.nom !== "NovaForge" ? etat.nom : "");
  const [mdp, setMdp] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const connecter = async () => {
    setEnvoi(true);
    try {
      const r = await fetch(`${API_URL}/api/agent/email/connexion`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ adresse, nom, mot_de_passe: mdp }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Connexion impossible");
      setMdp("");
      toast.success(`Gmail connecté : ${d.adresse}`);
      onConnecte();
    } catch (e) { toast.error(e.message); } finally { setEnvoi(false); }
  };
  return (
    <section className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-5">
      <h2 className="flex items-center gap-2 font-bold text-slate-900"><Plug className="h-5 w-5 text-indigo-600" /> Connecter Gmail</h2>
      <p className="mt-1 text-sm text-slate-600">
        Les emails partiront de cette boîte et apparaîtront dans ses « Envoyés ». Colle le mot de passe
        d'application (16 lettres) créé dans le compte Google › Sécurité › Mots de passe des applications.
        Il est vérifié auprès de Gmail, puis gardé sur le serveur (fichier accessible au seul administrateur système) et jamais réaffiché.
      </p>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        <input className={champ} value={adresse} onChange={(e) => setAdresse(e.target.value)} placeholder="Adresse Gmail" />
        <input className={champ} value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Nom affiché (ex. CCRM)" />
        <input className={`${champ} sm:col-span-2 font-mono`} type="password" autoComplete="new-password" value={mdp}
          onChange={(e) => setMdp(e.target.value)} placeholder="Mot de passe d'application (xxxx xxxx xxxx xxxx)" />
      </div>
      <button onClick={connecter} disabled={envoi || !mdp.trim()}
        className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
        {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Connecter et vérifier
      </button>
    </section>
  );
}

function Redaction({ token, initial, projets, onEnvoye, onFerme }) {
  const [e, setE] = useState({ a: "", cc: "", objet: "", texte: "", project_id: "", ...initial });
  const [pieces, setPieces] = useState([]);
  const [envoi, setEnvoi] = useState(false);
  const maj = (k, v) => setE((o) => ({ ...o, [k]: v }));

  const joindre = async (fichiers) => {
    const lus = await Promise.all(Array.from(fichiers || []).map((f) => new Promise((ok) => {
      const lecteur = new FileReader();
      lecteur.onload = () => ok({ nom: f.name, contenu: String(lecteur.result).split(",")[1] });
      lecteur.readAsDataURL(f);
    })));
    setPieces((p) => [...p, ...lus]);
  };

  const envoyer = async () => {
    setEnvoi(true);
    try {
      const r = await fetch(`${API_URL}/api/agent/email/envoyer`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ...e, project_id: e.project_id || null, proposition_id: initial?.id || null, pieces }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Envoi impossible");
      toast.success("Email envoyé");
      onEnvoye();
    } catch (err) { toast.error(err.message); } finally { setEnvoi(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30" onClick={onFerme}>
      <aside className="h-full w-full max-w-2xl overflow-y-auto bg-white p-6 shadow-2xl" onClick={(ev) => ev.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">{initial?.id ? "Relire et envoyer" : "Nouvel email"}</h2>
          <button onClick={onFerme} className="rounded-lg p-1.5 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        {initial?.source && <p className="mt-1 text-xs text-slate-500">Rédigé par {initial.source}</p>}
        <div className="mt-4 space-y-2">
          <input className={champ} value={e.a} onChange={(ev) => maj("a", ev.target.value)} placeholder="À (plusieurs adresses séparées par une virgule)" />
          <input className={champ} value={e.cc} onChange={(ev) => maj("cc", ev.target.value)} placeholder="Cc" />
          <input className={champ} value={e.objet} onChange={(ev) => maj("objet", ev.target.value)} placeholder="Objet" />
          <textarea className={champ} rows={14} value={e.texte} onChange={(ev) => maj("texte", ev.target.value)} placeholder="Message" />
          <select className={champ} value={e.project_id || ""} onChange={(ev) => maj("project_id", ev.target.value)}>
            <option value="">Rattacher à un projet (facultatif)</option>
            {projets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-slate-600">
            <Paperclip className="h-4 w-4" /> Joindre des fichiers
            <input type="file" multiple className="hidden" onChange={(ev) => { joindre(ev.target.files); ev.target.value = ""; }} />
          </label>
          {pieces.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {pieces.map((p, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs">
                  {p.nom}<button onClick={() => setPieces((l) => l.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
        </div>
        <button onClick={envoyer} disabled={envoi || !e.a.trim() || !e.objet.trim() || !e.texte.trim()}
          className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
          {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer
        </button>
      </aside>
    </div>
  );
}

export default function EmailsPage() {
  const { token } = useAuth();
  const [etat, setEtat] = useState(null);
  const [emails, setEmails] = useState(null);
  const [projets, setProjets] = useState([]);
  const [redaction, setRedaction] = useState(null);
  const [test, setTest] = useState("");
  const h = { Authorization: `Bearer ${token}` };

  const charger = useCallback(async () => {
    const [a, b] = await Promise.all([
      fetch(`${API_URL}/api/agent/email/etat`, { headers: h }).then((r) => r.json()),
      fetch(`${API_URL}/api/agent/email`, { headers: h }).then((r) => r.json()),
    ]);
    setEtat(a); setEmails(Array.isArray(b) ? b : []);
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    charger();
    fetch(`${API_URL}/api/projects`, { headers: h }).then((r) => r.json()).then((d) => setProjets(Array.isArray(d) ? d : [])).catch(() => {});
    // eslint-disable-next-line
  }, [charger]);

  const tester = async () => {
    const r = await fetch(`${API_URL}/api/agent/email/tester`, {
      method: "POST", headers: { ...h, "Content-Type": "application/json" }, body: JSON.stringify({ a: test || null }),
    });
    const d = await r.json();
    if (r.ok) toast.success(d.envoye_a ? `Email de test envoyé à ${d.envoye_a}` : "Connexion Gmail valide");
    else toast.error(d.detail || "Test impossible");
  };
  const rejeter = async (id) => {
    await fetch(`${API_URL}/api/agent/email/${id}/rejeter`, { method: "POST", headers: h });
    charger();
  };

  const aValider = (emails || []).filter((e) => e.statut === "a_valider");
  const envoyes = (emails || []).filter((e) => e.statut === "envoye");

  return (
    <CRMLayout>
      <div className="p-6 lg:p-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900"><Mail className="h-6 w-6 text-indigo-600" /> Emails</h1>
            <p className="mt-1 text-sm text-slate-500">Écrire depuis NovaForge et valider les emails rédigés par les agents.</p>
          </div>
          {etat?.configure && (
            <button onClick={() => setRedaction({})} className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white">
              <Send className="h-4 w-4" /> Nouvel email
            </button>
          )}
        </div>

        {!etat ? <Loader2 className="mt-6 h-5 w-5 animate-spin text-slate-400" /> : !etat.configure ? (
          <div className="mt-5"><Connexion etat={etat} token={token} onConnecte={charger} /></div>
        ) : (
          <div className="mt-5 flex flex-wrap items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <span>Gmail connecté : <b>{etat.nom}</b> &lt;{etat.adresse}&gt;</span>
            <input value={test} onChange={(e) => setTest(e.target.value)} placeholder="Envoyer un test à…"
              className="ml-auto rounded-lg border border-emerald-200 bg-white px-2 py-1 text-sm" />
            <button onClick={tester} className="rounded-lg border border-emerald-300 bg-white px-3 py-1 text-sm">Tester</button>
          </div>
        )}

        {aValider.length > 0 && (
          <section className="mt-6">
            <h2 className="text-sm font-bold uppercase tracking-wide text-amber-700">À valider · {aValider.length} rédigé(s) par les agents</h2>
            <div className="mt-2 grid gap-2 lg:grid-cols-2">
              {aValider.map((e) => (
                <div key={e.id} className="rounded-xl border border-amber-200 bg-white p-4">
                  <p className="text-xs text-slate-500">{e.source}{e.project_name ? ` · ${e.project_name}` : ""}</p>
                  <p className="mt-1 font-semibold text-slate-900">{e.objet}</p>
                  <p className="text-xs text-slate-500">À : {e.a}</p>
                  <p className="mt-2 line-clamp-4 whitespace-pre-wrap text-sm text-slate-700">{e.texte}</p>
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => setRedaction(e)} disabled={!etat?.configure}
                      className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40">
                      <Send className="h-3.5 w-3.5" /> Relire et envoyer
                    </button>
                    <button onClick={() => rejeter(e.id)} className="rounded-lg border px-3 py-1.5 text-xs text-red-600">Rejeter</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-6">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Envoyés</h2>
          {!envoyes.length ? <p className="mt-2 text-sm text-slate-500">Aucun email envoyé depuis NovaForge pour l'instant.</p> : (
            <div className="mt-2 divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
              {envoyes.map((e) => (
                <div key={e.id} className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <span>{new Date(e.envoye_at).toLocaleString("fr-FR")}</span>
                    <span>· à {e.a}</span>
                    {e.project_name && <span>· {e.project_name}</span>}
                    {e.pieces_noms?.length > 0 && <span>· 📎 {e.pieces_noms.length}</span>}
                  </div>
                  <p className="mt-0.5 text-sm font-medium text-slate-900">{e.objet}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
      {redaction && <Redaction token={token} initial={redaction.id ? redaction : undefined} projets={projets}
        onFerme={() => setRedaction(null)} onEnvoye={() => { setRedaction(null); charger(); }} />}
    </CRMLayout>
  );
}
